// 장비 수치를 상용 게임식으로 표현하는 읽기 전용 모델.
// 규칙과 밸런스는 gear/mods/world가 소유하고, 이 파일은 실제 적용 결과와 비교 의미만 번역한다.

import { resolveGear, setOf, setCount, SLOTS } from './gear.js';
import { gearMods, gearWeaponStats } from './mods.js';
import { MERGE, mergeDayIn, neutralDayIn } from './dayin.js';
import { ORBIT, PASU, UNIT } from './config.js';

const EPS = 1e-9;

const METRICS = Object.freeze({
  coreDps:       { label: '주력 위력/초', dp: 1, better: 'high', betterText: '강해짐', worseText: '약해짐' },
  directDps:     { label: '직격 위력/초', dp: 1, better: 'high', betterText: '강해짐', worseText: '약해짐' },
  damage:        { label: '한 발 위력', dp: 1, better: 'high', betterText: '강해짐', worseText: '약해짐' },
  rate:          { label: '초당 발사', dp: 2, unit: '/초', better: 'high', betterText: '빨라짐', worseText: '느려짐' },
  shots:         { label: '한 번에', dp: 0, unit: '발', better: 'high', betterText: '늘어남', worseText: '줄어듦' },
  range:         { label: '사거리', dp: 1, better: 'high', betterText: '길어짐', worseText: '짧아짐' },
  spread:        { label: '흩어짐', dp: 2, better: 'low', betterText: '촘촘해짐', worseText: '퍼짐' },
  // 최종 DPS 배율이 아니라, 세 짝 부품이 돌의 피해 성장을 얼마나 전달하는지다.
  // 퍼센트만 보고 종합 화력으로 오해하지 않도록 화면 이름도 계산 대상을 그대로 말한다.
  support:       { label: '돌 피해 전달', dp: 0, percent: true, better: 'high', betterText: '잘 전달됨', worseText: '덜 전달됨' },
  effectiveTier: { label: '돌 실효 배율', dp: 2, prefix: '×', better: 'high', betterText: '높아짐', worseText: '낮아짐' },
  orbitDps:      { label: '궤도 위력/초', dp: 1, better: 'high', betterText: '강해짐', worseText: '약해짐' },
  orbitInnerDps: { label: '궤도 내측/초', dp: 1, better: 'high', betterText: '강해짐', worseText: '약해짐' },
  orbitHit:      { label: '궤도 1회 위력', dp: 1, better: 'high', betterText: '강해짐', worseText: '약해짐' },
  orbitRate:     { label: '궤도 타격', dp: 2, unit: '/초', better: 'high', betterText: '빨라짐', worseText: '느려짐' },
  orbitRadius:   { label: '궤도 반경', dp: 1, better: 'high', betterText: '넓어짐', worseText: '좁아짐' },
  orbitBand:     { label: '강타 대역', dp: 0, percent: true, better: 'high', betterText: '두꺼워짐', worseText: '얇아짐' },
  orbitCap:      { label: '동시 타격', dp: 0, unit: '마리', better: 'high', betterText: '늘어남', worseText: '줄어듦', infinity: '제한 없음' },
  // 교체 비교 전용 통합값: 한 발사 묶음의 유한 관통 총량 ↔ 손에 익은 돌(Infinity).
  // 발당값만 비교하면 다발의 공통 몫 `1 / shots` 때문에 낮은 등급이 더 좋아 보인다.
  // 실제 발사 묶음의 기대 총량 `pierce × shots`로 비교해야 발 수 성장까지 함께 읽힌다.
  // 상세 성능은 아래 두 정본 항목을 돌마다 하나만 골라 보여 중복시키지 않는다.
  pierceState:   { label: '묶음 관통', dp: 2, trim: true, better: 'high', betterText: '늘어남', worseText: '줄어듦', infinity: '무한', zero: '없음', targetValue: true },
  pierceInf:     { label: '관통', dp: 0, better: 'high', betterText: '무한 관통', worseText: '관통 해제', boolean: ['없음', '무한'] },
  stonePierce:   { label: '추가 관통', dp: 0, prefix: '+', better: 'high', betterText: '늘어남', worseText: '줄어듦' },
  lineGap:       { label: '선 간격', dp: 2, better: 'low', betterText: '촘촘해짐', worseText: '성겨짐' },
  skips:         { label: '튐 횟수', dp: 0, unit: '회', better: 'high', betterText: '늘어남', worseText: '줄어듦' },
  shards:        { label: '발당 평균 파편', dp: 1, unit: '개', better: 'high', betterText: '늘어남', worseText: '줄어듦' },
  shardChain:    { label: '파편 연쇄', dp: 0, unit: '세대', better: 'high', betterText: '깊어짐', worseText: '얕아짐' },
  splash:        { label: '착탄 반경', dp: 1, better: 'high', betterText: '넓어짐', worseText: '좁아짐' },
  splashAgain:   { label: '추가 폭발', dp: 0, unit: '회', better: 'high', betterText: '늘어남', worseText: '줄어듦' },
});

const BASE_ORDER = [
  'coreDps', 'damage', 'rate', 'shots', 'range', 'spread', 'orbitDps',
];

const SLOT_ORDER = Object.freeze({
  stone:  ['pierceState', 'coreDps', 'damage', 'rate', 'shots', 'range', 'effectiveTier', 'orbitDps'],
  // 짝 부품 후보는 실제 주력 위력/초를 먼저 말하고, 그 원인인 돌 피해 전달을 바로 뒤에 둔다.
  // 상품 카드의 별도 전달 경고와 대표 위력행이 함께 보여 퍼센트를 종합 화력으로 오해하지 않는다.
  // 돌 자체의 등급 비교는 위 stone 순서에서 기존 effectiveTier를 그대로 유지한다.
  cradle: ['coreDps', 'support', 'damage', 'shots', 'spread', 'orbitDps'],
  cord:   ['coreDps', 'support', 'range', 'spread', 'orbitRadius', 'orbitDps'],
  loop:   ['coreDps', 'support', 'rate', 'orbitDps', 'orbitHit', 'orbitRate'],
});

const SPECIALTY = Object.freeze({
  jandol: ['shots', 'stonePierce'],
  goreun: ['pierceInf', 'lineGap'],
  napjak: ['skips'],
  monan:  ['shards', 'shardChain'],
  chadol: ['orbitHit', 'orbitInnerDps', 'orbitRate', 'orbitRadius', 'orbitBand', 'orbitCap'],
  mugeo:  ['splash', 'splashAgain'],
});

const PROJECTILE_ONLY = new Set(['directDps', 'damage', 'rate', 'shots', 'range', 'spread', 'stonePierce']);

function defOf(key) {
  const def = METRICS[key];
  if (!def) throw new Error(`알 수 없는 장비 성능 항목: ${key}`);
  return def;
}

function sameValue(a, b) {
  if (a === b) return true;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= EPS;
}

function displayNumber(value, def) {
  if (value === Infinity) return def.infinity ?? '제한 없음';
  if (def.boolean) return def.boolean[value ? 1 : 0];
  if (!Number.isFinite(value)) return '—';
  if (value === 0 && def.zero) return def.zero;
  if (def.percent) return `${Math.round(value * 100)}%`;
  const fixed = def.trim ? String(Number(value.toFixed(def.dp))) : value.toFixed(def.dp);
  if (def.prefix) return `${def.prefix}${fixed}`;
  return `${fixed}${def.unit ?? ''}`;
}

function displayMagnitude(a, b, def) {
  if (!Number.isFinite(a) || !Number.isFinite(b) || def.boolean) return '변경';
  const d = Math.abs(b - a);
  if (def.percent) return `${Math.round(d * 100)}%p`;
  if (def.prefix) return `${def.prefix}${d.toFixed(def.dp)}`;
  return `${d.toFixed(def.dp)}${def.unit ?? ''}`;
}

function compactBeforeAfter(a, b, def) {
  if (!Number.isFinite(a) || !Number.isFinite(b) || def.boolean) {
    return `${displayNumber(a, def)} → ${displayNumber(b, def)}`;
  }
  if (def.percent) return `${Math.round(a * 100)} → ${Math.round(b * 100)}%`;
  const af = def.trim ? String(Number(a.toFixed(def.dp))) : a.toFixed(def.dp);
  const bf = def.trim ? String(Number(b.toFixed(def.dp))) : b.toFixed(def.dp);
  if (def.prefix) return `${def.prefix}${af} → ${def.prefix}${bf}`;
  return `${af} → ${bf}${def.unit ?? ''}`;
}

function contextOf(input = 1) {
  const src = typeof input === 'number' ? { axis: input } : (input ?? {});
  const axis = Number.isFinite(src.axis) ? src.axis : 1;
  return { axis, dayIn: { ...neutralDayIn(), ...(src.dayIn ?? {}) } };
}

export function metricText(key, value) {
  return displayNumber(value, defOf(key));
}

// 화면과 검사가 같은 문장을 읽는 세트 진단. 발동 판정 자체는 gear.setOf가 소유하고,
// 여기서는 실패했을 때 다음 행동만 사실대로 번역한다.
export function setRequirementStatus(gear) {
  const active = setOf(gear);
  if (active) {
    const label = active.level === 'diamond' ? '◆ 완성 세트'
      : active.level === 'large' ? '큰 세트' : '작은 세트';
    return {
      active: true,
      kind: active.level,
      set: active,
      short: `『${active.ko}』 ${label} 발동`,
      detail: active.level === 'diamond' ? (active.diamondFx ?? active.fx) : active.fx,
    };
  }

  const count = setCount(gear);
  if (!count.id || count.n < 2) {
    return { active: false, kind: 'none', count, short: '', detail: '' };
  }
  if (count.n < 4) {
    const missing = 4 - count.n;
    return {
      active: false,
      kind: 'parts',
      count,
      short: `세트 후보: ${count.ko} ${count.n}/4 · 같은 수식어 ${missing}칸 필요`,
      detail: '네 부위의 수식어가 모두 같아야 합니다.',
    };
  }

  const tiers = SLOTS.map((slot) => gear?.[slot]?.t ?? 1);
  if (tiers.some((tier) => tier < 4)) {
    return {
      active: false,
      kind: 'low',
      count,
      short: `세트 후보: ${count.ko} 4/4 · 등급 부족`,
      detail: '네 부위의 최저 등급이 4성 이상이어야 합니다.',
    };
  }
  // 같은 수식어 4/4 + 최저 4성은 setOf에서 반드시 발동한다. 여기 도달한다면 판정
  // 정본과 진단이 갈라진 것이므로, 존재하지 않는 별도 실패 이유를 만들지 않는다.
  return { active: false, kind: 'none', count, short: '', detail: '' };
}

// 실제 적용 코드가 증명하는 규칙만 제공한다. MERGE가 바뀌면 마지막 문장도 함께 바뀌어
// 잠정 결합 규칙을 확정 규칙처럼 남기지 않는다.
export function combatRuleCopy() {
  return Object.freeze({
    formula: '기본 위력/초 = 한 발 위력 × 한 번에 발 수 × 초당 발사',
    rate: '초당 발사: 장비 연사 계산 → 장비 상한 적용 → 느려짐 벌점과 낮 연사 배율 반영',
    rateCap: '장비 연사 상한은 기본 4발/초이며, 성긴 세트만 상한을 완화합니다.',
    pierce: '가벼운 돌: 공통 관통(1÷최종 발 수)에 돌 고유 추가 관통 +1을 더합니다. 발 수가 늘어도 한 몸씩은 확정으로 뚫고, 고유 +1 위에 공통 관통 총량 1회도 발사 묶음 전체에 보존됩니다. 단단한 돌(차돌)은 투사체가 없고, 손에 익은 돌은 무한 관통입니다.',
    crit: '일반 공격에는 확률 치명타가 없습니다.',
    bear: '곰은 급소 창 동안 배후에서 맞으면 소년 무기 피해 ×3 — 무작위가 아닌 위치 기믹입니다.',
    grip: Object.freeze([
      '모든 급 조합 장착. 별 등급 전달 = 30% + 70% × min(1, 세 짝 별 등급 배수 평균 ÷ 돌 별 등급 배수).',
      '급 물림 = 30%+70%×세 짝 급 비율 평균(동급 이상 1·하급 1/2·2/3·1/3). 돌 피해 전달 = 따로 계산한 별 등급 전달×급 물림(각 최저 30%).',
      '최종 위력/초 비율 아님. 낮은 급 투사 걸이: 연사·궤도 타격·화면 회전에 걸이 전달(30% + 70% × 급 비율)을 한 번 더 적용(차돌 제외).',
    ]),
    sets: Object.freeze([
      '같은 수식어 4부위 + 최저 4성 이상 = 세트 발동',
      '최저 4성(4성과 5성/◆ 혼합 포함) = 작은 세트',
      '같은 수식어 4부위 + 모두 5성/◆ = 큰 세트',
      '◆×4 = ◆ 완성 세트',
      '5성과 ◆ 혼합은 큰 세트 유지',
    ]),
    merge: MERGE === 'mul'
      ? '현재 규칙: 낮에서 넘어온 배율과 밤 장비·요령 배율은 곱연산으로 합칩니다.'
      : '현재 규칙: 낮에서 넘어온 배율과 밤 장비·요령 배율은 가산으로 합칩니다.',
  });
}

// 화살표와 색은 둘 다 숫자의 증감이 아니라 플레이어에게 좋은가/나쁜가를 말한다.
// 따라서 흩어짐 1.30→1.09는 숫자가 내려가도 초록 ▲다.
export function metricChange(key, from, to) {
  const def = defOf(key);
  if (sameValue(from, to)) return null;
  const numericUp = to > from;
  const better = def.better === 'low' ? !numericUp : numericUp;
  const fromText = displayNumber(from, def);
  const toText = displayNumber(to, def);
  return {
    key,
    ko: def.label,
    label: def.label,
    from: fromText,
    to: toText,
    beforeAfter: `${fromText} → ${toText}`,
    comparison: compactBeforeAfter(from, to, def),
    // 켬/끔 값을 카드 핵심행에서 모호한 「변경」으로 부르지 않는다. 특히 기존
    // 무한 관통 돌은 실제 정본인 「무한 관통/관통 해제」를 그대로 말해야 한다.
    delta: def.targetValue ? toText
      : def.boolean ? (better ? def.betterText : def.worseText)
      : displayMagnitude(from, to, def),
    rawDirection: numericUp ? 'increase' : 'decrease',
    trend: better ? 'better' : 'worse',
    tone: better ? 'good' : 'bad',
    arrow: better ? '▲' : '▼',
    impact: better ? def.betterText : def.worseText,
    // 구 호출부 호환. 표현층은 `arrow`/`tone`만 읽어 숫자 방향과 성능 방향을 섞지 않는다.
    up: numericUp,
    good: better,
  };
}

// world.refreshWeapon + world.autoWeapon + world.orbitStrike가 실제로 쓰는 식을 그대로 모은다.
// 낮에서 넘어온 배율도 같은 mergeDayIn 경로로 합쳐 현재 전투와 후보 비교가 같은 기준을 쓴다.
export function combatSnapshot(gear, context = 1) {
  const { axis, dayIn } = contextOf(context);
  const g = resolveGear(gear);
  const m = mergeDayIn(gearMods(gear), dayIn);
  const ws = gearWeaponStats(m, PASU, axis);
  const meleeOnly = !!m.meleeOnly;

  const projectileRate = meleeOnly ? 0 : (ws.rate * dayIn.fireRateMul) / (m.slowMul || 1);
  const projectileDamage = meleeOnly ? 0 : PASU.dmg * m.dmgMul;
  const projectiles = meleeOnly ? 0 : ws.projectiles;
  // gearWeaponStats.range가 base×축×장비×낮을 모두 한 번씩 합친 최종 사거리다.
  const projectileRange = meleeOnly ? 0 : ws.range / UNIT;
  const directDps = projectileDamage * projectiles * projectileRate;

  const orbitTick = m.orbitTick > 0 ? m.orbitTick : ORBIT.tick;
  const orbitRate = Math.max(0.2, m.orbitRate) / orbitTick;
  const orbitHit = PASU.dmg * m.orbitDmg * (m.orbitFull ? 1 : ORBIT.dmgRatio);
  const orbitDps = orbitHit * orbitRate;
  // ◆ 차돌은 매 6번째 틱에 내측도 전량 피해를 준다. 상세표는 특정 한 틱이 아니라
  // 지속 DPS이므로 한 주기의 실제 평균 비율을 쓴다.
  const sweepEvery = Math.max(0, m.orbitSweepEvery | 0);
  const orbitInnerRatio = m.orbitBand > 0
    ? (sweepEvery > 0 ? ((sweepEvery - 1) * ORBIT.discRatio + 1) / sweepEvery
      : ORBIT.discRatio)
    : 1;
  const orbitInnerHit = orbitHit * orbitInnerRatio;
  const orbitInnerDps = orbitInnerHit * orbitRate;

  return {
    stoneId: g.stoneId,
    meleeOnly,
    coreDps: meleeOnly ? orbitDps : directDps,
    directDps,
    damage: projectileDamage,
    rate: projectileRate,
    shots: projectiles,
    range: projectileRange,
    spread: g.spreadMul,
    support: g.support,
    effectiveTier: g.effectiveTierMul,
    orbitDps,
    orbitInnerDps,
    orbitHit,
    orbitRate,
    orbitRadius: (ORBIT.r * m.orbitR + ORBIT.pad) / UNIT,
    orbitBand: m.orbitBand,
    orbitCap: m.orbitCap === Infinity ? Infinity : Math.max(1, Math.round(ORBIT.cap * m.orbitCap)),
    // 비교용 관통 상태는 고유 태그가 아니라 실제 한 발에 실리는 최종 기대값이다.
    // 공통 바닥 1/shots도 숨기되 낮은 등급의 발당 분수가 이득처럼 보이지 않도록,
    // 교체 비교에는 실제 한 발사 묶음의 기대 관통 총량을 쓴다.
    pierceState: meleeOnly ? 0 : (m.pierceInf ? Infinity : g.pierce * g.shots),
    pierceInf: m.pierceInf ? 1 : 0,
    stonePierce: meleeOnly || m.pierceInf ? 0 : g.stonePierce,
    lineGap: m.lineGap,
    skips: m.skips,
    shards: m.shards,
    shardChain: m.shardChain,
    splash: m.landSplash,
    splashAgain: m.splashAgain,
    set: g.set,
    setSeen: g.setSeen,
    tierBySlot: g.tierBySlot,
    diamondSlots: g.diamondSlots,
  };
}

function specialtyKeys(stoneId) {
  return SPECIALTY[stoneId] ?? [];
}

function uniqueKeys(keys) {
  return [...new Set(keys)];
}

export function performanceRows(gear, context = 1) {
  const stats = combatSnapshot(gear, context);
  const base = stats.meleeOnly
    ? ['coreDps', 'orbitHit', 'orbitInnerDps', 'orbitRate', 'orbitRadius', 'orbitBand', 'orbitCap', 'support']
    : ['coreDps', 'damage', 'rate', 'shots', 'range', 'spread', 'support', 'orbitDps'];
  return uniqueKeys([...base, ...specialtyKeys(stats.stoneId)]).map((key) => {
    const def = defOf(key);
    const label = key === 'coreDps'
      ? (stats.meleeOnly ? '궤도 강타/초' : '기본 위력/초')
      : key === 'orbitDps' && !stats.meleeOnly ? '보조 궤도/초'
      : key === 'orbitHit' && stats.meleeOnly ? '궤도 강타 1회'
      : def.label;
    return { key, label, value: stats[key], text: displayNumber(stats[key], def) };
  });
}

export function compareLoadouts(currentGear, nextGear,
  { slot = null, axis = 1, dayIn = null, maxRows = 15 } = {}) {
  const context = { axis, dayIn };
  const current = combatSnapshot(currentGear, context);
  const next = combatSnapshot(nextGear, context);
  const bothMelee = current.meleeOnly && next.meleeOnly;
  const ordered = uniqueKeys([
    ...(SLOT_ORDER[slot] ?? []),
    ...specialtyKeys(current.stoneId),
    ...specialtyKeys(next.stoneId),
    ...BASE_ORDER,
  ]);
  const allRows = [];
  for (const key of ordered) {
    if (bothMelee && PROJECTILE_ONLY.has(key)) continue;
    // 관통은 두 장비가 모두 투사체를 쏠 때만 서로 비교할 수 있다. 차돌 전환에서
    // `1 → 없음`을 띄우면 투사체 자체가 사라진 결과를 관통 성능 하락처럼 중복해서
    // 말한다. 그 전환은 이미 한 발·발수·사거리 0으로 명확히 보이고, 대신 차돌의
    // 실제 핵심인 강타 대역·동시 타격까지 15줄 안에 빠짐없이 남긴다.
    if (key === 'pierceState' && (current.meleeOnly || next.meleeOnly)) continue;
    // 차돌 전환에서는 coreDps가 곧 차돌의 orbitDps다. 같은 최종값을 두 줄로 되풀이하면
    // 실제 돌 피해 전달 행을 포함한 15행 기하에서 한 줄이 숨는다. 궤도 1회·주기·반경은
    // 바로 뒤 상세행에 모두 남으므로 전환 때의 중복 orbitDps만 생략한다.
    if (key === 'orbitDps' && current.meleeOnly !== next.meleeOnly) continue;
    // 교체에서는 실제 묶음 총량↔무한을 pierceState 한 줄로 완전히 표현한다. 두 원시 항목을
    // 또 내면 무한→1이 「관통 해제 + 관통 증가」라는 모순된 두 줄로 보인다.
    if (key === 'pierceInf' || key === 'stonePierce') continue;
    const change = metricChange(key, current[key], next[key]);
    if (change) allRows.push(change);
  }
  const limit = Number.isFinite(maxRows) ? Math.max(0, maxRows) : allRows.length;
  const rows = allRows.slice(0, limit);
  const primary = metricChange('coreDps', current.coreDps, next.coreDps);
  return {
    current,
    next,
    primary,
    totalChanges: allRows.length,
    hiddenChanges: Math.max(0, allRows.length - rows.length),
    verdict: primary
      ? `${primary.label} ${primary.beforeAfter} · ${primary.impact}`
      : '주력 위력 변화 없음',
    rows,
    allRows,
  };
}

export const GEAR_STAT_METRICS = METRICS;
export const GEAR_SPECIALTY_KEYS = SPECIALTY;
